import { ResourceTimeline } from '../../services/api'

export interface FlatDay {
  key: string // "YYYY-MM-DD"
  count: number
  itemsBefore: number
}

export interface FlatMonth {
  key: string // "YYYY-MM"
  count: number
  itemsBefore: number
  /** local first-of-month, for Intl label formatting */
  date: Date
}

export interface FlatYear {
  key: string // "YYYY"
  count: number
  itemsBefore: number
}

export interface TimelineIndex {
  total: number
  days: Array<FlatDay>
  months: Array<FlatMonth>
  years: Array<FlatYear>
  dayStartIndex(dayKey: string): number | undefined
  flatIndexToDay(flatIndex: number): FlatDay | undefined
  fractionToMonth(fraction: number): FlatMonth | undefined
  monthToFraction(month: FlatMonth): number
}

/** last bucket with itemsBefore <= flatIndex */
const bucketAt = <T extends { itemsBefore: number }>(buckets: Array<T>, flatIndex: number): T | undefined => {
  if (buckets.length === 0) return undefined
  let low = 0
  let high = buckets.length - 1
  while (low < high) {
    const mid = Math.ceil((low + high) / 2)
    if (buckets[mid].itemsBefore <= flatIndex) low = mid
    else high = mid - 1
  }
  return buckets[low]
}

const monthKeyToDate = (key: string): Date => {
  const [year, month] = key.split('-').map(Number)
  return new Date(year, (month || 1) - 1, 1)
}

// Buckets arrive newest-first in the exact order of the /resources list,
// so prefix sums over them are flat list offsets.
export const buildTimelineIndex = (timeline: ResourceTimeline): TimelineIndex => {
  const days: Array<FlatDay> = []
  const months: Array<FlatMonth> = []
  const years: Array<FlatYear> = []
  const dayStartByKey = new Map<string, number>()
  let itemsBefore = 0

  for (const year of timeline.years) {
    years.push({ key: year.key, count: year.count, itemsBefore })
    for (const month of year.months) {
      months.push({
        key: month.key,
        count: month.count,
        itemsBefore,
        date: monthKeyToDate(month.key),
      })
      for (const day of month.days) {
        days.push({ key: day.key, count: day.count, itemsBefore })
        dayStartByKey.set(day.key, itemsBefore)
        itemsBefore += day.count
      }
    }
  }

  const total = timeline.total

  return {
    total,
    days,
    months,
    years,
    dayStartIndex: (dayKey) => dayStartByKey.get(dayKey),
    flatIndexToDay: (flatIndex) => bucketAt(days, Math.max(0, Math.min(flatIndex, total - 1))),
    fractionToMonth: (fraction) => {
      if (total === 0) return undefined
      const clamped = Math.max(0, Math.min(1, fraction))
      const flatIndex = Math.min(Math.floor(clamped * total), total - 1)
      return bucketAt(months, flatIndex)
    },
    monthToFraction: (month) => (total === 0 ? 0 : month.itemsBefore / total),
  }
}
